import { useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

/**
 * Returns the effective search params, taking into account background location.
 * When a modal is open (background location exists), returns the background location's search params.
 * Otherwise, returns the current location's search params.
 *
 * This is useful for maintaining correct query keys when rendering components in background mode
 * (e.g., when a modal is open over a list page).
 */
export function useEffectiveSearchParams(): URLSearchParams {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return useMemo(() => {
    // Check if there's a background location (modal is open)
    const backgroundLocation = location.state?.background as
      | { pathname: string; search: string }
      | undefined;

    // Use background search params if available, otherwise use current search params
    if (backgroundLocation?.search) {
      return new URLSearchParams(backgroundLocation.search);
    }

    return searchParams;
  }, [location.state?.background, searchParams]);
}
